import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { format, subDays, setHours, setMinutes, setSeconds } from 'date-fns';
import * as Yup from 'yup';
import { isEmpty, includes } from 'lodash';

import { t, parseDateString } from '~/helpers';
import { Report, ListTransfer } from '~/components';
import { DateTime, Autocomplete, RadioGroup } from '~/components/Form';
import { GFO, GFO_CONSULTA } from '~/constants/perfil';

export default function RelListagemImpedimento() {
  const [open, setOpen] = useState(true);
  const [organ, setOrgan] = useState({ orga_codigo: '', orga_nome: '' });
  const [product, setProduct] = useState({});
  const [units, setUnits] = useState([]);

  const { glousuariologin, glousuariotipo, glousuarioorgacodigo } = useSelector(
    (state) => state.user.profile
  );

  const cells = useMemo(
    () => [
      { id: 'unid_nome', label: 'Unidade' },
      { id: 'unid_codigo', label: 'Código' },
    ],
    []
  );

  const datahorainicial = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(subDays(new Date(), 7), 0), 0), 0),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const datahorafinal = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(new Date(), 23), 59), 59),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const handleCloseReport = useCallback(() => {
    setOpen(false);
  }, []);

  const handleValidate = useCallback(
    async (data) => {
      try {
        const schema = Yup.object().shape({
          datahorainicial: Yup.date()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .transform((_, value) => parseDateString(value))
            .required(t('s.geral.validacao.campoobrigatorio')),
          datahorafinal: Yup.date()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .transform((_, value) => parseDateString(value))
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(
              datahorafinal,
              `A data não pode ser superior a ${datahorafinal}`
            )
            .min(
              Yup.ref('datahorainicial'),
              'A data não pode ser inferior a Data inicial'
            ),
          orgao: Yup.string()
            .nullable()
            .required(t('s.geral.validacao.campoobrigatorio')),
        });

        await schema.validate(data, { abortEarly: false });

        if (isEmpty(units))
          throw 'Por favor, selecione ao menos uma unidade antes de enviar.';
      } catch (err) {
        const form = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            form[error.path] = error.message.replace(`${error.path} `, '');
          });

          throw { form };
        } else {
          throw err;
        }
      }
    },
    [units, datahorafinal]
  );

  const onSerializeData = useCallback(
    (data) => {
      return {
        ...data,
        orgaonome: organ.orga_nome,
        orgao: organ.orga_codigo ? `${organ.orga_codigo}` : '',
        produto: product.prod_codigo ? `${product.prod_codigo}` : '',
        produtostr: product.prod_descricao ? product.prod_descricao : '',
        usuarioAleatorio: glousuariologin,
        ...units.reduce(
          (a, b, key) =>
            Object.assign(a, { [`units${key}`]: `${b.unid_codigo}` }),
          {}
        ),
      };
    },
    [organ, product, units]
  );

  useEffect(() => {
    setUnits([]);
  }, [organ]);

  return (
    <Report
      open={open}
      requestType="POST"
      onClose={handleCloseReport}
      serialize={onSerializeData}
      validate={handleValidate}
      title="Consumo Combustível Órgão Período - Unidades"
      relatorio="CONSUMOCOMBUSTIVELORGAOPERIODOUNIDADES"
      maxWidth="md"
      initials={{
        datahorainicial,
        datahorafinal,
        orgao: glousuarioorgacodigo || '',
        formato: 'pdf',
      }}
    >
      <DateTime
        time
        disableFuture
        label="Data Inicial"
        name="datahorainicial"
      />

      <DateTime
        time
        disableFuture
        label="Data Final"
        name="datahorafinal"
        maxDateMessage=""
      />

      <Autocomplete
        name="orgao"
        label="orga_nome"
        title="Órgão"
        url="orgaos"
        unique="orga_codigo"
        getValue={(value) => setOrgan(value)}
        disabled={includes([GFO, GFO_CONSULTA], glousuariotipo)}
      />

      <Autocomplete
        name="produto"
        label="prod_descricao"
        title="Produto"
        url="produtos"
        unique="prod_codigo"
        getValue={(value) => setProduct(value)}
      />

      <ListTransfer
        search
        grid
        cells={cells}
        url="unidades"
        label="unid_nome"
        unique="unid_codigo"
        leftTitle="Unidades disponíveis"
        rightTitle="Unidades selecionadas"
        onCallback={(value) => setUnits(value)}
        maxWidth={38}
        maxHeight={200}
        reset={isEmpty(units)}
        query={{
          wherefield: ['orga_codigo'],
          valuewherefield: [organ.orga_codigo],
          typewherefield: ['EQUALS'],
        }}
        showCount
      />

      <RadioGroup
        row
        name="tipo"
        label="Formato"
        options={[
          { label: 'PDF', value: 'pdf' },
          { label: 'Excel', value: 'xls' },
        ]}
        defaultValue="pdf"
      />
    </Report>
  );
}
